import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  Avatar,
} from '@mui/material';
import {
  DirectionsCar as CarIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../index';
import { useAuth } from '../context/AuthContext';
import { useFavorites } from '../context/FavoritesContext';
import FavoriteButton from '../components/FavoriteButton';

function Favorites() {
  const { isAnonymous } = useAuth();
  const { favorites, favoritesLoading } = useFavorites();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Fetch vehicle data for each favorited ID
  useEffect(() => {
    if (favorites.length === 0) {
      setVehicles([]);
      return;
    }

    const fetchVehicles = async () => {
      setLoading(true);
      const results = [];
      for (const vehicleId of favorites) {
        try {
          const vehicleDoc = await getDoc(doc(db, 'vehicles', vehicleId));
          if (vehicleDoc.exists()) {
            results.push({ id: vehicleDoc.id, ...vehicleDoc.data() });
          } else {
            // Vehicle may have been removed from Firestore; keep a stub
            results.push({ id: vehicleId, heading: vehicleId, price: null });
          }
        } catch (err) {
          console.error('Error fetching favorite vehicle:', vehicleId, err);
          results.push({ id: vehicleId, heading: vehicleId, price: null });
        }
      }
      setVehicles(results);
      setLoading(false);
    };

    fetchVehicles();
  }, [favorites]);

  const isLoading = favoritesLoading || loading;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Favorites
      </Typography>

      {isAnonymous && favorites.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Guest favorites are stored in this browser only and will be lost if you clear your data.{' '}
          <RouterLink to="/register">Create a free account</RouterLink> to sync across devices.
        </Alert>
      )}

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : favorites.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CarIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No favorites yet
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Search for vehicles and tap the heart icon to save them here.
          </Typography>
          <Button
            variant="contained"
            startIcon={<SearchIcon />}
            component={RouterLink}
            to="/search"
          >
            Search Vehicles
          </Button>
        </Paper>
      ) : (
        <Paper>
          <List>
            {vehicles.map((vehicle) => (
              <ListItem
                key={vehicle.id}
                button
                onClick={() => navigate(`/vehicle/${vehicle.id}`)}
                divider
              >
                <ListItemAvatar>
                  {vehicle.photo_url ? (
                    <Avatar
                      variant="rounded"
                      src={vehicle.photo_url}
                      sx={{ width: 64, height: 48 }}
                    />
                  ) : (
                    <Avatar variant="rounded" sx={{ width: 64, height: 48, bgcolor: 'grey.200' }}>
                      <CarIcon color="disabled" />
                    </Avatar>
                  )}
                </ListItemAvatar>
                <ListItemText
                  sx={{ ml: 1 }}
                  primary={vehicle.heading || `${vehicle.year || ''} ${vehicle.make || ''} ${vehicle.model || ''}`}
                  secondary={
                    [
                      vehicle.price ? `$${vehicle.price.toLocaleString()}` : null,
                      vehicle.mileage ? `${vehicle.mileage.toLocaleString()} mi` : null,
                      [vehicle.city, vehicle.state].filter(Boolean).join(', ') || null,
                    ]
                      .filter(Boolean)
                      .join(' · ')
                  }
                />
                <ListItemSecondaryAction>
                  <FavoriteButton vehicleId={vehicle.id} size="medium" />
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  );
}

export default Favorites;
